/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2025, Zumper
 * @description ZoeContentInput
 */

import { Search } from '@zcode-ui/icons'
import { Button, Input, cn } from '@zcode-ui/shadz-ui'
import {
  ZOE_DEFAULT_QUESTION_SUGGESTION_CLICK,
  ZOE_NO_RESULT,
  ZOE_SAVED_QUESTION_SUGGESTION_CLICK,
  ZOE_SEARCH_QUERY_SEND,
} from '@zcode-ui/web-zumper-integrations'
import { EVENT_NAMES, PROP_NAMES } from '@zcode/mixpanel'
import { type Origin, localStorageSingleton } from '@zcode/nr-utils'
import type { AIAssistantFiltersResponse, Dispatch, GetState } from '@zcode/state-type'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackMapListZoeEvent } from '../module/thunks/trackMapListZoeEvent'
import { AskZoeLoadingState } from './AskZoeLoadingState'
import { SearchItem } from './SearchItem'

const EXAMPLE_SEARCHES = [
  '1-2 bedroom that is pet friendly',
  'Studio that is good for work-from-home',
  'Anything with a rental deal',
]

export const ZoeContentInput = ({
  getAIAssistantSearchFilters,
  handleClose,
  isDrawer,
  isFromSearchBar,
  mixpanelEvent,
}: {
  getAIAssistantSearchFilters: (
    searchQuery: string
  ) => (dispatch: Dispatch, getState: GetState) => Promise<AIAssistantFiltersResponse | undefined>
  handleClose: () => void
  isDrawer?: boolean
  isFromSearchBar?: boolean
  mixpanelEvent?: (
    event: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES],
    origin: Origin | undefined,
    extraPayload?: object
  ) => (dispatch: Dispatch, getState: GetState) => void
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()

  let recentSearches: string[] = localStorageSingleton.get('zoeRecentSearches') ?? []
  const hasRecentSearches = !!recentSearches.length
  const [hasNoResult, setHasNoResult] = useState(false)

  const handleSubmit = async (searchText?: string, searchTextType?: 'recent' | 'suggestion') => {
    const search = searchText ?? inputValue

    if (!search) {
      return
    }

    if (searchTextType) {
      const mixpanelEventName =
        searchTextType === 'suggestion'
          ? EVENT_NAMES.ZOE_SAVED_QUESTION_SUGGESTION_CLICK
          : EVENT_NAMES.ZOE_DEFAULT_QUESTION_SUGGESTION_CLICK
      dispatch(
        trackMapListZoeEvent({
          eventName:
            searchTextType === 'suggestion'
              ? ZOE_SAVED_QUESTION_SUGGESTION_CLICK
              : ZOE_DEFAULT_QUESTION_SUGGESTION_CLICK,
          additionalProps: {
            text: search,
            searchType: isFromSearchBar ? 'Search bar click' : 'Filter bar CTA',
          },
        })
      )
      mixpanelEvent &&
        dispatch(
          mixpanelEvent(mixpanelEventName, undefined, {
            [PROP_NAMES.BUTTON_CLICKED]: isFromSearchBar ? 'Search bar click' : 'Filter bar CTA',
          })
        )
    }

    dispatch(
      trackMapListZoeEvent({
        eventName: ZOE_SEARCH_QUERY_SEND,
        additionalProps: {
          text: search,
          searchType: isFromSearchBar ? 'Search bar click' : 'Filter bar CTA',
        },
      })
    )
    mixpanelEvent &&
      dispatch(
        mixpanelEvent(EVENT_NAMES.ZOE_SEARCH_QUERY_SEND, undefined, {
          [PROP_NAMES.BUTTON_CLICKED]: isFromSearchBar ? 'Search bar click' : 'Filter bar CTA',
        })
      )
    setHasNoResult(false)

    if (isDrawer) {
      const div = document.getElementById('zoe-content-input')
      if (div) {
        div.setAttribute('tabindex', '-1')
        div.focus()
      }
    }

    setIsLoading(true)
    const result = await dispatch(getAIAssistantSearchFilters(search))
    setIsLoading(false)
    if (!result) {
      setHasNoResult(true)
      dispatch(
        trackMapListZoeEvent({
          eventName: ZOE_NO_RESULT,
          additionalProps: {
            text: 'With natural light',
          },
        })
      )
      mixpanelEvent && dispatch(mixpanelEvent(EVENT_NAMES.ZOE_NO_RESULT, undefined))
    } else {
      if (!recentSearches.includes(search)) {
        if (recentSearches.length === 3) {
          recentSearches = [search, ...recentSearches.slice(0, -1)]
        } else {
          recentSearches = [search, ...recentSearches]
        }
      }
      localStorageSingleton.set('zoeRecentSearches', recentSearches)
      handleClose()
    }
  }

  return (
    <div
      id="zoe-content-input"
      className={isDrawer ? 'focus-visible:outline-none px-2' : undefined}
    >
      <div className="relative flex gap-3 px-4 pt-4">
        <form
          className="w-full flex gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <div className="relative w-full">
            <Input
              className={cn(
                'h-14 [&::placeholder]:text-gray-300 border-gray-150 [&[type=text]]:pl-[14px] [&[type=text]]:py-4 text-base [&[type=text]]:leading-6 [&[type=text]]:pr-20',
                isDrawer && '[&[type=text]]:pl-[42px]'
              )}
              enterKeyHint="search"
              onChange={(e) => {
                setInputValue((e.currentTarget ?? e.target).value)
              }}
              placeholder="What are you looking for?"
              value={inputValue}
              type="text"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-800 bg-gray-250 text-xs font-bold rounded p-[4px] tracking-widest leading-none">
              BETA
            </span>
          </div>
          {isDrawer && (
            <div className="absolute pt-1 left-8 top-[45%]">
              <Search fill="#726F6C" />
            </div>
          )}
          {!isDrawer && (
            <Button className="w-[56px] h-14 min-w-[56px]" variant="secondary" type="submit">
              <Search fill="#fff" />
            </Button>
          )}
        </form>
      </div>
      {hasNoResult && (
        <div className="text-base font-extrabold leading-6 text-gray-950 mx-4 pt-8 pb-6 border-b border-solid border-gray-150">
          Sorry, Zoe can't answer that. Please try a different search.
        </div>
      )}
      {isLoading && (
        <div className={cn('mt-[88px] mb-[88px] mx-auto w-[234px]', isDrawer && 'mt-[244px]')}>
          <AskZoeLoadingState />
        </div>
      )}
      <div className={cn('pb-4 overflow-y-scroll', !isDrawer && 'max-h-[372px] overflow-y-scroll')}>
        {!isLoading && hasRecentSearches && (
          <div className={hasNoResult ? 'mt-6' : 'mt-8'}>
            <p className="px-4 text-xs text-gray-600 font-medium leading-4 tracking-[1.68px] uppercase">
              Recent
            </p>
            <div className="flex flex-col mt-4">
              {recentSearches.map((example) => {
                return (
                  <SearchItem
                    key={example}
                    handleSubmit={handleSubmit}
                    isRecentSearch
                    searchText={example}
                  />
                )
              })}
            </div>
          </div>
        )}
        {!isLoading && (
          <div className="mt-8">
            <p className="px-4 text-xs text-gray-600 font-medium leading-4 tracking-[1.68px] uppercase">
              Example Searches
            </p>
            <div className="flex flex-col mt-4">
              {EXAMPLE_SEARCHES.map((example) => {
                return <SearchItem key={example} handleSubmit={handleSubmit} searchText={example} />
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
